import React from 'react';
import Icon from '../../../components/AppIcon';

const FeedbackAnalyticsSection = ({ data, loading }) => {
  const { averageRating, responseRate, trendingIssues } = data || {};

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating || 0);
    const hasHalfStar = (rating || 0) % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center gap-1">
        {[...Array(fullStars)]?.map((_, i) => (
          <Icon key={`full-${i}`} name="Star" className="w-5 h-5 text-yellow-500 fill-yellow-500" />
        ))}
        {hasHalfStar && (
          <Icon name="Star" className="w-5 h-5 text-yellow-500 fill-yellow-500 opacity-50" />
        )}
        {[...Array(emptyStars)]?.map((_, i) => (
          <Icon key={`empty-${i}`} name="Star" className="w-5 h-5 text-gray-300" />
        ))}
      </div>
    );
  };

  const getResponseRateColor = (rate) => {
    if (rate >= 70) return 'text-green-600';
    if (rate >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getResponseRateBgColor = (rate) => {
    if (rate >= 70) return 'bg-green-100';
    if (rate >= 40) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-1 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="MessageSquare" className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Análisis de Retroalimentación de Clientes
          </h2>
        </div>
        <div className="flex justify-center py-12">
          <Icon name="Loader2" className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-1 p-6">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="MessageSquare" className="w-6 h-6 text-blue-600" />
        <h2 className="text-xl font-heading font-semibold text-foreground">
          Análisis de Retroalimentación de Clientes
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Average Satisfaction Rating */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Star" className="w-5 h-5 text-blue-600" />
            <h3 className="text-sm font-medium text-blue-900">
              Calificación Promedio
            </h3>
          </div>
          <div className="mb-3">
            {renderStars(averageRating?.averageRating || 0)}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-blue-900">
              {averageRating?.averageRating?.toFixed(1) || '0.0'}
            </span>
            <span className="text-sm text-blue-700">/ 5.0</span>
          </div>
          <p className="text-xs text-blue-700 mt-2">
            Basado en {averageRating?.totalFeedback || 0} evaluaciones
          </p>
        </div>

        {/* Follow-up Response Rate */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 border border-green-200">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="CheckCircle" className="w-5 h-5 text-green-600" />
            <h3 className="text-sm font-medium text-green-900">
              Tasa de Seguimiento
            </h3>
          </div>
          <div className="mb-3">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full transition-all duration-500 ${
                  (responseRate?.responseRate || 0) >= 70
                    ? 'bg-green-600'
                    : (responseRate?.responseRate || 0) >= 40
                    ? 'bg-yellow-600' :'bg-red-600'
                }`}
                style={{ width: `${responseRate?.responseRate || 0}%` }}
              />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className={`text-3xl font-bold ${getResponseRateColor(responseRate?.responseRate || 0)}`}>
              {responseRate?.responseRate?.toFixed(1) || '0.0'}%
            </span>
          </div>
          <p className="text-xs text-green-700 mt-2">
            {responseRate?.totalRequested || 0} de {responseRate?.totalFeedback || 0} solicitan seguimiento
          </p>
        </div>

        {/* Trending Service Issues */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-5 border border-orange-200">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="TrendingUp" className="w-5 h-5 text-orange-600" />
            <h3 className="text-sm font-medium text-orange-900">
              Problemas Frecuentes
            </h3>
          </div>
          {trendingIssues && trendingIssues?.length > 0 ? (
            <div className="space-y-2">
              {trendingIssues?.slice(0, 5)?.map((issue, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-white rounded-md px-3 py-2 border border-orange-200"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-orange-600 bg-orange-100 rounded-full w-5 h-5 flex items-center justify-center">
                      {index + 1}
                    </span>
                    <span className="text-sm font-medium text-orange-900 capitalize">
                      {issue?.issue}
                    </span>
                  </div>
                  <span className="text-xs font-bold text-orange-700 bg-orange-200 rounded-full px-2 py-0.5">
                    {issue?.frequency}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6">
              <Icon name="CheckCircle" className="w-8 h-8 text-orange-400 mb-2" />
              <p className="text-sm text-orange-700 text-center">
                No hay problemas frecuentes reportados
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackAnalyticsSection;
